import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import type {
  AdminToolsEndpointConfig,
  ConfigApiError,
  EnvironmentKey,
} from '@web-config-app/core';
import { ConfigEntityAuthAction, OperationMethod } from '@web-config-app/core';
import {
  API_JSON_CONTENT_TYPE,
  buildConfigEntityAuthHeaders,
  createEndpointFetch,
} from '@web-config-app/api';
import { useEnvironmentFetch } from '../use-environment-fetch/use-environment-fetch';

export type UseAdminToolsSchemaSubmitOptions = Pick<
  UseMutationOptions<unknown, ConfigApiError>,
  'onSuccess' | 'onError'
>;

export interface UseAdminToolsSchemaSubmitProps {
  environment: EnvironmentKey;
  data: Record<string, unknown>;
  endpoint: AdminToolsEndpointConfig;
  headers?: HeadersInit;
  options?: UseAdminToolsSchemaSubmitOptions;
}

/**
 * Generic submit hook for admin schema pages.
 * Builds JSON:API payload using endpoint config and form data.
 */
export const useAdminToolsSchemaSubmit = ({
  environment,
  data,
  endpoint,
  headers = {
    'Content-Type': API_JSON_CONTENT_TYPE,
  },
  options,
}: UseAdminToolsSchemaSubmitProps) => {
  const currentEnvironmentFetch = useEnvironmentFetch(environment);

  const endpointFetch = createEndpointFetch(
    { path: endpoint.path, method: endpoint.method ?? OperationMethod.POST },
    currentEnvironmentFetch,
  );

  const mutationFn = async () => {
    // `environment` is used for request context only and should not be sent as an attribute
    const { environment: unusedEnvironment, ...attributes } = data;

    const body = JSON.stringify({
      data: {
        type: endpoint.resourceType,
        attributes,
      },
    });

    const domainEntityType = data.domainEntityType as string | undefined;
    const configEntityAuthHeaders =
      domainEntityType && endpoint.path === '/v1/backfill-config-entities'
        ? buildConfigEntityAuthHeaders(
            domainEntityType,
            ConfigEntityAuthAction.Create,
          )
        : {};

    return endpointFetch({
      headers: { ...headers, ...configEntityAuthHeaders },
      body,
    });
  };

  const mutation = useMutation({
    mutationFn,
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  });

  return mutation;
};
